import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, clearToken } from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import type { ApiKeyListItem, AiKeyResponse, AiKeyUsageSummary } from '@quicktl/shared';

export default function Account() {
  const navigate = useNavigate();

  const [apiKeys, setApiKeys] = useState<ApiKeyListItem[]>([]);
  const [aiKeys, setAiKeys] = useState<AiKeyResponse[]>([]);
  const [loadingApi, setLoadingApi] = useState(true);
  const [loadingAi, setLoadingAi] = useState(true);
  const [error, setError] = useState('');
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [showAddAiKey, setShowAddAiKey] = useState(false);
  const [aiKeyForm, setAiKeyForm] = useState({ provider: 'claude', apiKey: '', label: '' });
  const [creatingAiKey, setCreatingAiKey] = useState(false);
  const [usage, setUsage] = useState<AiKeyUsageSummary | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [usagePeriod, setUsagePeriod] = useState<'7d' | '30d' | 'all'>('30d');

  useEffect(() => {
    api.listApiKeys().then(r => setApiKeys(r.data)).catch((e: any) => {
      if (e.status === 401) { clearToken(); navigate('/login'); return; }
      setError(e.body?.error || 'Failed to load API keys');
    }).finally(() => setLoadingApi(false));
    api.listAiKeys().then(r => setAiKeys(r.data)).catch((e: any) => {
      if (e.status === 401) { clearToken(); navigate('/login'); return; }
      setError(e.body?.error || 'Failed to load AI keys');
    }).finally(() => setLoadingAi(false));
  }, []);

  useEffect(() => {
    setLoadingUsage(true);
    const filters: { from?: string } = {};
    if (usagePeriod === '7d') filters.from = new Date(Date.now() - 7 * 86400000).toISOString();
    else if (usagePeriod === '30d') filters.from = new Date(Date.now() - 30 * 86400000).toISOString();
    api.getAiUsage(filters).then(setUsage).catch(() => {}).finally(() => setLoadingUsage(false));
  }, [usagePeriod]);

  async function handleCreateApiKey(e: React.FormEvent) {
    e.preventDefault();
    if (!newApiKeyName.trim()) return;
    setCreatingApiKey(true);
    setError('');
    try {
      const result = await api.createApiKey({ name: newApiKeyName.trim() });
      setCreatedApiKey(result.key);
      setApiKeys([
        { id: result.key, name: result.name, keyPrefix: result.keyPrefix, createdAt: new Date().toISOString(), lastUsedAt: null },
        ...apiKeys,
      ]);
      setNewApiKeyName('');
    } catch (e: any) {
      setError(e.body?.error || 'Failed to create API key');
    } finally {
      setCreatingApiKey(false);
    }
  }

  async function handleDeleteApiKey(id: string) {
    if (!confirm('Delete this API key? Any integrations using it will stop working.')) return;
    try {
      await api.deleteApiKey(id);
      setApiKeys(apiKeys.filter(k => k.id !== id));
    } catch (e: any) {
      setError(e.body?.error || 'Failed to delete API key');
    }
  }

  async function handleCreateAiKey(e: React.FormEvent) {
    e.preventDefault();
    setCreatingAiKey(true);
    setError('');
    try {
      const result = await api.createAiKey(aiKeyForm);
      setAiKeys([result, ...aiKeys]);
      setAiKeyForm({ provider: 'claude', apiKey: '', label: '' });
      setShowAddAiKey(false);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to add AI key');
    } finally {
      setCreatingAiKey(false);
    }
  }

  async function handleDeleteAiKey(id: string) {
    if (!confirm('Delete this AI provider key?')) return;
    try {
      await api.deleteAiKey(id);
      setAiKeys(aiKeys.filter(k => k.id !== id));
    } catch (e: any) {
      setError(e.body?.error || 'Failed to delete AI key');
    }
  }

  return (
    <DashboardLayout>
        <h2 className="text-xl font-semibold mb-6">Account</h2>

        {error && (
          <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-8">
          {/* API Keys */}
          <section>
            <h3 className="text-sm font-medium text-slate-300 mb-3">API Keys</h3>

            {createdApiKey && (
              <div role="alert" className="mb-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                <span className="font-medium">Key created — copy it now, it won't be shown again:</span>
                <code className="block mt-1 font-mono text-xs break-all select-all">{createdApiKey}</code>
                <button onClick={() => setCreatedApiKey(null)} className="mt-2 text-xs text-green-500 hover:text-green-300">
                  Dismiss
                </button>
              </div>
            )}

            <form onSubmit={handleCreateApiKey} className="flex gap-2 mb-3">
              <label htmlFor="new-api-key-name" className="sr-only">API key name</label>
              <input
                id="new-api-key-name"
                type="text"
                placeholder="Key name (e.g. CI/CD)"
                value={newApiKeyName}
                onChange={e => setNewApiKeyName(e.target.value)}
                required
                className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                type="submit"
                disabled={creatingApiKey}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                {creatingApiKey ? 'Creating…' : 'Create'}
              </button>
            </form>

            {loadingApi ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : apiKeys.length === 0 ? (
              <p className="text-slate-500 text-sm">No API keys.</p>
            ) : (
              <div className="space-y-1">
                {apiKeys.map(k => (
                  <div key={k.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-800 text-sm">
                    <div>
                      <span className="font-medium">{k.name}</span>
                      <span className="ml-2 font-mono text-xs text-slate-500">{k.keyPrefix}…</span>
                    </div>
                    <button
                      onClick={() => handleDeleteApiKey(k.id)}
                      aria-label={`Delete API key ${k.name}`}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* AI Provider Keys */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-300">Claude API Keys</h3>
              <button
                onClick={() => setShowAddAiKey(!showAddAiKey)}
                className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                {showAddAiKey ? 'Cancel' : 'Add key'}
              </button>
            </div>

            {showAddAiKey && (
              <form onSubmit={handleCreateAiKey} className="mb-3 p-4 rounded-lg border border-slate-800 bg-slate-900 space-y-3">
                <div>
                  <label htmlFor="ai-key-label" className="sr-only">Label</label>
                  <input
                    id="ai-key-label"
                    type="text"
                    placeholder="Label (e.g. Production)"
                    value={aiKeyForm.label}
                    onChange={e => setAiKeyForm({ ...aiKeyForm, label: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label htmlFor="ai-key-value" className="sr-only">API key</label>
                  <input
                    id="ai-key-value"
                    type="password"
                    placeholder="API key"
                    value={aiKeyForm.apiKey}
                    onChange={e => setAiKeyForm({ ...aiKeyForm, apiKey: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={creatingAiKey}
                    className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
                  >
                    {creatingAiKey ? 'Adding…' : 'Add key'}
                  </button>
                </div>
              </form>
            )}

            {loadingAi ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : aiKeys.length === 0 ? (
              <p className="text-slate-500 text-sm">No Claude API keys. Add one to enable AI translation.</p>
            ) : (
              <div className="space-y-1">
                {aiKeys.map(k => (
                  <div key={k.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-800 text-sm">
                    <div>
                      <span className="font-medium">{k.label}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteAiKey(k.id)}
                      aria-label={`Delete AI key ${k.label}`}
                      className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* AI Usage */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-300">AI Usage</h3>
              <div className="flex gap-1">
                {(['7d', '30d', 'all'] as const).map(p => (
                  <button
                    key={p}
                    onClick={() => setUsagePeriod(p)}
                    className={`text-xs px-2 py-1 rounded transition-colors ${usagePeriod === p ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    {p === '7d' ? '7 days' : p === '30d' ? '30 days' : 'All time'}
                  </button>
                ))}
              </div>
            </div>

            {loadingUsage ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : !usage || usage.totalRequests === 0 ? (
              <p className="text-slate-500 text-sm">No AI usage recorded yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="p-3 rounded-lg border border-slate-800">
                  <p className="text-xs text-slate-500">Requests</p>
                  <p className="text-lg font-semibold">{usage.totalRequests}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-800">
                  <p className="text-xs text-slate-500">Keys translated</p>
                  <p className="text-lg font-semibold">{usage.totalKeysTranslated.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-800">
                  <p className="text-xs text-slate-500">Tokens (in / out)</p>
                  <p className="text-lg font-semibold">{usage.totalInputTokens.toLocaleString()} <span className="text-slate-500 text-sm">/</span> {usage.totalOutputTokens.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-800">
                  <p className="text-xs text-slate-500">Estimated cost</p>
                  <p className="text-lg font-semibold">${usage.estimatedCostUsd.toFixed(4)}</p>
                </div>
              </div>
            )}
          </section>
        </div>
    </DashboardLayout>
  );
}
